import { reactive } from 'vue'
import { getMentionIdsForMessage, waitIfKey } from '../helperMethods'
import store from './store'
import { setVideosession, getChat, setChat } from './chats'
import { getMyUserId, setUser, getMyUser, updateUser } from './memberships'
import { MESSAGE_PAGE_SIZE } from '@/helpers/constants'
import moment from 'moment'
import {
	normalizeMediaItem,
	storage,
	denormalizeMediaItem,
	getLocalDate,
} from '@/helpers/utils'
import { timedLogger } from '@/helpers/log'
import { importTable } from '@/store/database'

/* Glossary:
	- "Parent Message": Any original message referenced by child messages in a thread
	- "Child Message": All messages in a thread, other than the original parent message
*/

const messagesById = reactive({})
const messagesByBubbleId = reactive({})
const messagesByTaskId = reactive({})
const messagesByThreadId = reactive({})
const cursorsByBubbleId = reactive({})
const pollsById = reactive({})

export const messagesData = {
	messagesById,
	messagesByBubbleId,
	messagesByTaskId,
	messagesByThreadId,
	cursorsByBubbleId,
}

function sortMessageIds(id1, id2) {
	if (!Number.isInteger(id1)) return 1 // sending or failed
	if (!Number.isInteger(id2)) return -1 // sending or failed
	return id1 - id2
}

const normalizeReactions = (reactions, reactionsummary) => {
	// REST API sends reactions as an array, old RPC sends reactionsummary
	const list = Array.isArray(reactions) ? reactions : reactionsummary
	if (!list || !list.length) {
		// Already-normalized object (e.g. from pusher update re-normalization)
		if (reactions && Object.keys(reactions).length > 0) return { reactions }
		return {}
	}
	const myId = getMyUserId()
	return {
		reactions: list.reduce((acc, { users, count, emoji, emoji_base }) => {
			// users can come back null from the API
			const userIds = users || []
			const me = userIds.includes(myId)
			acc[emoji] = { users: userIds, me, count, emoji, emoji_base }
			return acc
		}, {}),
	}
}

const normalizeReplies = (
	parentmessage,
	message,
	{ skipStore = false } = {}
) => {
	if (!skipStore) setMessage(parentmessage)
	const threadId = getThreadIdFromMessage(message)
	const key = message.id || message.uuid
	if (!skipStore && threadId !== key) {
		messagesByThreadId[threadId] = {
			...(messagesByThreadId[threadId] || {}),
			[key]: key,
		}
	}
	return {
		threadId,
		isParent: isParentMessage(message),
	}
}

export const normalizeMedia = ({ messagemedia = [], attachments = [] }) => {
	const items = attachments?.length
		? attachments
		: messagemedia?.length
		? messagemedia
		: []
	const mediaCount = items.length || 0
	const normalized = items.map(normalizeMediaItem).sort((a, b) => {
		if (a.id && b.id) return a.id - b.id
		return 0
	})
	return {
		hasMedia: mediaCount > 0,
		mediaCount,
		media: normalized[0],
		attachments: normalized,
		messagemedia: normalized,
	}
}

const normalizeTranslation = (message) => {
	let { id, messagetrans, translations, user_edited_version } = message
	messagetrans = messagetrans ?? (translations?.length ? translations : null)
	const previous = getMessage(id)
	const isTranslated = message.isTranslated ?? previous?.isTranslated ?? false
	if (!messagetrans && previous?.messagetrans) {
		if (previous.user_edited_version === user_edited_version) {
			// messagetrans is not included in MessageUpdated events so
			// we need to persist it if the previous version had one and
			// assuming the user_edited_version hasn't changed
			messagetrans = previous.messagetrans
		}
	}
	if (Array.isArray(messagetrans)) {
		// if there are multiple translations, we'll need to find the one
		// in the list that matches our preferred language
		const me = getMyUser()
		messagetrans = messagetrans.find(
			(mt) => (mt.lang ?? mt.language) === me.language
		)
		// normalize lang field for downstream consumers (REST uses 'language', RPC uses 'lang')
		if (messagetrans && !messagetrans.lang) {
			messagetrans = { ...messagetrans, lang: messagetrans.language }
		}
	}
	return { isTranslated, messagetrans, translations: messagetrans } // TODO: remove translations alias once the migration is complete
}

const normalizeSendState = (message) => {
	const sendState = message.sendState || 'sent'
	return {
		sendState,
		isWaiting: sendState === 'waiting',
		isSending: sendState === 'sending',
		isFailed: sendState === 'failed',
		isSent: sendState === 'sent',
	}
}

const normalizePoll = (message) => {
	// ignore any messages without a poll
	if (!message.poll_id) return {}
	// if the message already has a poll, add it to the store
	if (message.poll) {
		setPoll(message.poll)
		return {}
	}
	// if the message has a poll but no poll object, try to fetch it from the store
	const poll = getPoll(message.poll_id)
	if (!poll) return {}
	return { poll }
}

const normalizePollTranslation = (poll) => {
	let { isTranslated = false, canTranslate = false } = poll

	const me = getMyUser()
	const myLangs = [me.language, ...(me.languages || [])]
	const iUnderstandLang = myLangs.includes(poll.language)
	canTranslate =
		poll.language && poll.language !== me.language && !iUnderstandLang

	return { ...poll, isTranslated, canTranslate }
}

// REST serializes every date as ISO-8601 with an offset
// (`2026-10-29T08:00:00+00:00`); RPC sends bare strings (`2026-10-29 08:00:00`).
// That difference is fine for the instants it mostly carries, but `due` and
// `reminder_local` are floating — the calendar day and wall-clock time the user
// picked, with no zone of their own (the absolute instant is kept separately,
// in `reminder_utc`). Given an offset, `moment(str)` dutifully converts into
// the viewer's zone, so an 8:00 AM reminder reads as 3:00 AM at UTC-5 and a due
// date rolls back a day.
//
// parseZone reads the string as written rather than converting it, and
// re-formatting drops the offset — leaving a value nothing downstream can
// shift, in either API's shape. Compare `getLocalDate` in helpers/utils, which
// adapts the mirror-image case: bare RPC strings that really are UTC instants.
const floatingDate = (value) => {
	if (!value) return value
	const parsed = moment.parseZone(value)
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value
}

const floatingDateTime = (value) => {
	if (!value) return value
	const parsed = moment.parseZone(value)
	return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value
}

export const normalizeTask = (task) => {
	if (!task) return task
	const bubble_id = task.bubble_id || task.chat_id
	const assigneeuser_id = task.assigneeuser_id ?? task.assignee_user_id
	const due = floatingDate(task.due || task.due_at)
	const completed = task.completed || task.completed_at
	const reminder_local = floatingDateTime(
		task.reminder_local || task.reminder_local_at
	)
	const uuid = task.uuid || task.local_id
	const taskmedia = task.taskmedia || task.attachments || []

	return {
		...task,
		bubble_id,
		chat_id: bubble_id, // TODO: remove this once the migration is complete
		assigneeuser_id,
		assignee_user_id: assigneeuser_id, // TODO: remove this once the migration is complete
		due,
		due_at: due, // TODO: remove this once the migration is complete
		completed,
		completed_at: completed, // TODO: remove this once the migration is complete
		reminder_local,
		reminder_local_at: reminder_local, // TODO: remove this once the migration is complete
		uuid,
		local_id: uuid, // TODO: remove this once the migration is complete
		taskmedia,
		attachments: taskmedia, // TODO: remove this once the migration is complete
	}
}

const normalizeResource = (resource) => {
	if (!resource) return resource
	const thumbnail_url = resource.thumbnail_url || resource.thumbnailurl
	const thumbnail_height = resource.thumbnail_height || resource.thumbnailheight
	const thumbnail_width = resource.thumbnail_width || resource.thumbnailwidth
	let provider_name = resource.provider_name || resource.providername
	if (!provider_name && resource.url) {
		try {
			const host = new URL(resource.url).hostname
			provider_name = host.replace(/^www\./i, '')
		} catch (e) {
			// invalid URL, skip
		}
	}
	return {
		...resource,
		thumbnail_url,
		thumbnailurl: thumbnail_url, // TODO: remove once migration complete
		thumbnail_height,
		thumbnailheight: thumbnail_height, // TODO: remove once migration complete
		thumbnail_width,
		thumbnailwidth: thumbnail_width, // TODO: remove once migration complete
		provider_name,
		providername: provider_name, // TODO: remove once migration complete
	}
}

export const normalizeMessage = (message, { skipStore = false } = {}) => {
	if (!message) return null
	const {
		user,
		task_id,
		reactions,
		reactionsummary = [],
		parent,
		parentmessage,
		meeting,
		videosession,
		...rest
	} = message
	const chat_id = rest.chat_id || rest.bubble_id
	if (!skipStore) {
		setVideosession(meeting || videosession)
		setUser(user)
	}
	const parentMsg = parent || parentmessage
	if (!skipStore) setMessage(parentMsg)
	const parent_id = rest.parent_id ?? rest.parentmessage_id ?? null // TODO: remove this once the migration is complete
	const first_child_id =
		rest.first_child_id ?? rest.firstchildmessage_id ?? null // TODO: remove this once the migration is complete
	const last_child_id = rest.last_child_id ?? rest.lastchildmessage_id ?? null // TODO: remove this once the migration is complete
	const system_event = rest.system_event ?? rest.systemevent ?? null // TODO: remove this once the migration is complete
	const text = rest.text || rest.message || '' // TODO: remove this once the migration is complete
	const local_id = rest.local_id || rest.uuid || rest.id
	const lang = rest.lang || rest.language // TODO: remove this once the migration is complete
	const system_message_parts =
		rest.system_message_parts || rest.systemmessageparts // TODO: remove this once the migration is complete

	if (!skipStore && rest.mentions?.length > 0) {
		rest.mentions
			.filter((u) => u && typeof u === 'object' && 'id' in u)
			.forEach((partialUser) => {
				// mentions are only partial users so we persist any existing data
				updateUser(partialUser.id, partialUser)
			})
	}

	// Thread chat: if the API sends a sparse thread object, persist it
	const thread_id = rest.thread_id || rest.thread?.id || null
	if (!skipStore && rest.thread?.id) {
		setChat(rest.thread)
	}
	delete rest.thread // prevent re-calling setChat on every setMessage
	delete rest.thread_id

	const task = normalizeTask(rest.task)
	const resource = normalizeResource(rest.resource)

	return {
		...rest,
		user,
		meeting,
		task,
		resource,
		key: rest.id || local_id,
		chat_id,
		bubble_id: chat_id, // TODO: remove this once the migration is complete
		task_id: task_id || rest.task?.id || null,
		message: rest.message || '',
		isEdited: !!(rest.user_edited_at || rest.edited_at),
		user_edited_at: rest.user_edited_at || rest.edited_at || null,
		meeting_id: rest.meeting_id || rest.videosession_id,
		videosession_id: rest.meeting_id || rest.videosession_id, // TODO: remove this once the migration is complete
		parent_id,
		parentmessage_id: parent_id, // TODO: remove this once the migration is complete
		first_child_id,
		firstchildmessage_id: first_child_id, // TODO: remove this once the migration is complete
		last_child_id,
		lastchildmessage_id: last_child_id, // TODO: remove this once the migration is complete
		system_event,
		systemevent: system_event, // TODO: remove this once the migration is complete
		text,
		message: text, // TODO: remove this once the migration is complete
		local_id,
		uuid: local_id, // TODO: remove this once the migration is complete
		lang,
		language: lang, // TODO: remove this once the migration is complete
		system_message_parts,
		systemmessageparts: system_message_parts, // TODO: remove this once the migration is complete
		mentions: getMentionIdsForMessage(rest),
		thread_id,
		...normalizeSendState(rest),
		...normalizeReactions(reactions, reactionsummary),
		...normalizeReplies(parentMsg, rest, { skipStore }),
		...normalizeMedia(rest),
		...normalizeTranslation(rest),
		...normalizePoll(rest),
	}
}

const customFields = [
	'hasMedia',
	'isEdited',
	'isFailed',
	'isParent',
	'isSending',
	'isSent',
	'isTranslated',
	'media',
	'meeting',
	'sendState',
	'threadId',
	'user',
]
export const denormalizeMessage = (message) => {
	message = Object.assign({}, message)
	customFields.forEach((key) => {
		delete message[key]
	})
	if (!message.task_id) {
		delete message.task_id
	}
	// generateMessagePayload() omits parentmessage_id entirely when not replying,
	// but the store sets it to null on every message — strip it when null so the
	// API doesn't reject it with INVALID_INTEGER_PARENTMESSAGE_ID on retry
	if (!message.parentmessage_id) {
		delete message.parentmessage_id
	}
	if (message.messagemedia?.length) {
		message.messagemedia = message.messagemedia.map(denormalizeMediaItem)
	}
	return message
}

const alertAboutChanges = (messages = []) => {
	// TODO: figure out a better way to alert Chat about changes
	// that doesn't involve EventBus
	EventBus.$emitDebounced(
		'message-change',
		messages.filter((m) => m).map((m) => m.id)
	)
}

const isOldVersion = (message) => {
	if (!message) return null
	const previous = getMessage(message.id)
	if (previous) {
		return moment(getLocalDate(previous.updated_at)).isAfter(
			getLocalDate(message.updated_at)
		)
	}
	return false
}

export const setMessage = (message, skipDB = false) => {
	if (!message || isOldVersion(message)) return null
	if (message.id) deleteMessage(message.uuid || message.local_id, true)
	message = normalizeMessage(message)
	const key = message.id || message.uuid
	if (message.bubble_id) {
		messagesByBubbleId[message.bubble_id] = {
			...(messagesByBubbleId[message.bubble_id] || {}),
			[key]: key,
		}
	}
	if (message.task_id) {
		messagesByTaskId[message.task_id] = {
			...(messagesByTaskId[message.task_id] || {}),
			[key]: key,
		}
	}
	messagesById[key] = message
	if (!skipDB) {
		if (message.id) {
			store.dbPut('messages', message)
		}
		alertAboutChanges([message])
	}
	return message
}

export const setMessages = (messages, skipDB) => {
	if (!messages) return null
	const normal = messages.map((msg) => setMessage(msg, true)).filter((m) => m)
	if (normal.length && !skipDB) {
		store.dbBulkPut('messages', normal)
		alertAboutChanges(normal)
	}
	return normal
}

export const setMedia = (media) => {
	if (!media || (!media.messageUUID && !media.message_id)) return null
	const msgId = media.messageUUID || media.message_id
	const message = getMessage(msgId)
	if (!message || !message.messagemedia?.length) return null
	media = normalizeMediaItem(media)
	const existingMedia = message.attachments || message.messagemedia // TODO: remove this once the migration is complete
	const updatedMedia = existingMedia.map((m) => {
		if (m.tempId === media.tempId || m.uuid === media.uuid) {
			return media
		}
		return m
	})
	message.attachments = updatedMedia
	message.messagemedia = updatedMedia // TODO: remove this once the migration is complete
	setMessage(message)
	return media
}

export const setPoll = (poll) => {
	if (!poll?.id) return null
	poll = normalizePollTranslation(poll)

	// Preserve the voted property from existing poll options if they exist
	const existingPoll = getPoll(poll.id)
	if (existingPoll?.options && poll?.options) {
		poll = {
			...poll,
			options: poll.options.map((newOption) => {
				const existingOption = existingPoll.options.find(
					(opt) => opt.id === newOption.id
				)
				// Only preserve voted if new option doesn't have it and existing option does
				if (
					newOption.voted === undefined &&
					existingOption?.voted !== undefined
				) {
					return {
						...newOption,
						voted: existingOption.voted,
					}
				}
				return newOption
			}),
		}
	}

	pollsById[poll.id] = poll
	EventBus.$emit('poll-change', poll.id)
	return poll
}

export const updateMessageWithPoll = (poll) => {
	if (!poll?.id || !poll.chat_id) return null
	const messages = getMessagesForBubble(poll.chat_id) || []
	let pollMessage = messages.find((m) => m.poll_id === poll.id)
	if (pollMessage) {
		return updateMessage(pollMessage.id, { poll })
	}
	return false
}

export const updatePoll = (id, data = {}) => {
	if (!id) return null
	const poll = getPoll(id) || { id }
	return setPoll({ ...poll, ...data })
}

export const getPoll = (id) => {
	return pollsById[id] || null
}

export const diffAndDeleteMessagesOutsideRange = (messages) => {
	if (!messages || !messages.length) return null
	const chatId = messages[0].chat_id || messages[0].bubble_id
	const log = timedLogger(`diffing messages for chat ${chatId}`)
	log('starting')
	// collect all of the message ids in this set
	const newMessageIds = messages.map((m) => m.id)
	// as well as the parent ids for any messages in this set
	const parentIds = new Set(
		messages.map((m) => m.parent_id || m.parentmessage_id).filter((x) => x)
	)
	const oldestIdInSet = Math.min(...newMessageIds)
	const newestIdInSet = Math.max(...newMessageIds)
	const pinnedMessageId = getChat(chatId)?.pinned_message_id
	// find any messages in the cache that aren't in these new sets
	// and delete them since they were likely deleted/archived on server

	const idsToDelete = getMessageIdsForBubble(chatId).filter((id) => {
		const hasId = Number.isInteger(id)
		const isInNewSet = newMessageIds.includes(id)
		const isParent = parentIds.has(id)
		const isOlderThanOldestInSet = id < oldestIdInSet
		const isWithinRange = id <= newestIdInSet && id >= oldestIdInSet
		const isPinned = id === pinnedMessageId
		if (!hasId) return false // keep all messages that have no id (only uuid)
		if (isInNewSet) return false // message is in server set, so its still valid
		if (isParent) return false // message is a parent of message in server set, so keep it
		if (isPinned) return false // message is pinned for group so keep it
		if (isOlderThanOldestInSet) return true // message is old, needs to be trimmed/deleted
		if (isWithinRange) return true // message is not in set and is within range, so it was deleted
		return false // message falls outside of range so we can't
	})

	log('deleting ids...', idsToDelete)
	deleteMessages(idsToDelete)
	log('done deleting')
	log.print()
}

export const deleteCachedMessagesNotInSet = (messages) => {
	if (!messages || !messages.length) return null
	const chatId = messages[0].chat_id || messages[0].bubble_id
	const log = timedLogger(`trimming messages cache for chat ${chatId}`)
	log('starting')
	// collect all of the message ids in this set
	const newMessageIds = messages.map((m) => m.id)
	// as well as the parent ids for any messages in this set
	const parentIds = new Set(
		messages.map((m) => m.parent_id || m.parentmessage_id).filter((x) => x)
	)
	const pinnedMessageId = getChat(chatId)?.pinned_message_id

	const idsToDelete = getMessageIdsForBubble(chatId).filter((id) => {
		const hasId = Number.isInteger(id)
		const isInNewSet = newMessageIds.includes(id)
		const isParent = parentIds.has(id)
		const isPinned = id === pinnedMessageId
		if (!hasId) return false // keep all messages that have no id (only uuid)
		if (isInNewSet) return false // message is in server set, so its still valid
		if (isParent) return false // message is a parent of message in server set, so keep it
		if (isPinned) return false // message is pinned for group so keep it
		return true // message does not existing in this set, so trim it
	})
	log('deleting ids...', idsToDelete)
	deleteMessages(idsToDelete)
	log('done deleting')
	log.print()
}

export const getMessage = (messageId) => {
	return messagesById[messageId] || null
}

export const getMessageIdsForBubble = (bubbleId) => {
	// key is a string, value is an int, so we want Object.values() here
	return Object.values(messagesByBubbleId[bubbleId] || {})
}

export const getSortedMessageIdsForBubble = (bubbleId) => {
	return getMessageIdsForBubble(bubbleId).sort(sortMessageIds)
}

export const getSortedMessageIds = () => {
	return getSortedMessageIdsForBubble(store.currentChatId)
}

export const getMessagesForBubble = (bubbleId) => {
	return getSortedMessageIdsForBubble(bubbleId).map(getMessage)
}

export const getMessageIds = () => {
	return getSortedMessageIdsForBubble(store.currentChatId)
}

export const getMessages = () => {
	const id = store.currentChatId
	const { start, stop } = getCursorsForBubble(id)
	return getMessagesForBubble(id).filter(
		(m) => !m.id || (m.id >= start && m.id <= stop)
	)
}

export const getNonSystemMessages = () => {
	return getMessages().filter((m) => m.systemevent === null)
}

export const getMostRecentMessageForBubble = (bubbleId) => {
	const [mostRecentId] = getSortedMessageIdsForBubble(bubbleId).slice(-1)
	return getMessage(mostRecentId)
}

export const getOldestMessageForBubble = (bubbleId) => {
	const [oldestId] = getSortedMessageIdsForBubble(bubbleId)
	return getMessage(oldestId)
}

export const getMostRecentMessage = () => {
	return getMostRecentMessageForBubble(store.currentChatId)
}

export const getOldestMessage = () => {
	return getOldestMessageForBubble(store.currentChatId)
}

export const getMessageIdsForTaskId = (taskId) => {
	return Object.values(messagesByTaskId[taskId] || {})
}

export const updateMessage = (id, dataOrUpdater = {}) => {
	if (!id) return null
	const message = getMessage(id) || { id }
	if (typeof dataOrUpdater === 'function') {
		return setMessage(dataOrUpdater(message))
	}
	return setMessage({ ...message, ...dataOrUpdater })
}

export const deleteMessage = (id, skipAlert) => {
	const message = getMessage(id)
	if (!message) return null
	delete messagesById[id]
	store.dbDeleteByKey('messages', id)
	if (message.bubble_id) {
		delete messagesByBubbleId[message.bubble_id][id]
	}
	if (messagesByThreadId[message.threadId]) {
		delete messagesByThreadId[message.threadId][id]
	}
	if (!skipAlert) {
		alertAboutChanges([message])
	}
}

export const deleteMessages = (ids) => {
	ids.forEach((id) => deleteMessage(id, true))
	alertAboutChanges([])
}

export const deleteOldestMessagesForBubble = (bubbleId) => {
	const messages = getMessagesForBubble(bubbleId)
	if (messages.length <= MESSAGE_PAGE_SIZE) return
	// messages are in reverse order, so we have to delete from the bottom
	let cursorIndex = messages.length - MESSAGE_PAGE_SIZE
	if (cursorIndex < 0) {
		cursorIndex = 0
	}
	const cursor = messages[cursorIndex].id
	const pinnedMessageId = getChat(bubbleId).pinned_message_id
	setStartCursorForBubble(bubbleId, cursor)
	const idsToDelete = messages
		.slice(0, cursorIndex)
		.filter((m) => !m.isParent && m.id !== pinnedMessageId)
		.map((m) => m.id)
	deleteMessages(idsToDelete)
}

export const importMessagesFromCacheForBubble = async (
	bubble_id,
	skipCursors
) => {
	if (isNaN(parseInt(bubble_id))) {
		log(`importMessagesFromCacheForBubble function passed bad ID ${bubble_id}`)
		return
	}
	let isHistoryComplete = false
	const onHistoryComplete = (bubbleId) => {
		// if message history fetch finishes first, then don't update from the cache
		if (bubble_id === bubbleId) {
			isHistoryComplete = true
		}
	}
	EventBus.$on('bubbleHistoryComplete', onHistoryComplete)
	// leaving this logging in here at debug level so we can diagnose
	//  any future issues that might arise with slow db performance
	const log = timedLogger(`messages cache for chat ${bubble_id}`)
	log('starting query')
	try {
		const messages = await importTable('messages', { where: { bubble_id } })
		await waitIfKey('slowDB', 8000)
		log('query complete')
		if (isHistoryComplete) {
			log('message history fetch finished first')
			return
		}
		log('updating store...')
		const normalized = setMessages(messages, true)
		log('store updated')
		if (!skipCursors) {
			const sortedIds = normalized.map((m) => m.id).sort(sortMessageIds)
			const [startId] = sortedIds
			const [stopId] = sortedIds.slice(-1)
			setCursorsForBubble(bubble_id, { start: startId, stop: stopId })
			log('cursors updated')
		}
		alertAboutChanges(normalized)
	} catch (error) {
		console.error('Cannot decrypt messages', error)
	}
	log.print()
	EventBus.$off('bubbleHistoryComplete', onHistoryComplete)
}

const resolveMessageOrId = (messageOrId) => {
	return messageOrId.uuid || messageOrId.id
		? messageOrId
		: getMessage(messageOrId)
}

export const isParentMessage = (messageOrId) => {
	const message = resolveMessageOrId(messageOrId)
	if (!message) return null
	return !!(message.firstchildmessage_id || message.first_child_id)
}

export const getThreadIdFromMessage = (messageOrId) => {
	const message = resolveMessageOrId(messageOrId)
	if (!message) return null
	const { threadId, parentmessage_id, parent_id, id, uuid } = message
	if (threadId) return threadId
	if (parentmessage_id || parent_id) return parentmessage_id || parent_id
	return id || uuid
}

export const getChildMessageIdsInThread = (threadId) => {
	// this will not include the parent/original message, only children
	return Object.values(messagesByThreadId[threadId] || {})
}

export const getChildMessagesInThread = (threadId) => {
	return getChildMessageIdsInThread(threadId)
		.sort(sortMessageIds)
		.map(getMessage)
}

export const getAllMessagesInThread = (threadId) => {
	if (!threadId) return []
	return [getMessage(threadId)].concat(getChildMessagesInThread(threadId))
}

export const getThreadForMessage = (messageOrId) => {
	const threadId = getThreadIdFromMessage(messageOrId)
	if (!threadId) return null
	return getAllMessagesInThread(threadId)
}

export const getCursorsForBubble = (bubbleId) => {
	return cursorsByBubbleId[bubbleId] || {}
}

export const getStartCursorForBubble = (bubbleId) => {
	return getCursorsForBubble(bubbleId).start
}

export const getStopCursorForBubble = (bubbleId) => {
	return getCursorsForBubble(bubbleId).stop
}

export const setCursorsForBubble = (bubbleId, cursors) => {
	cursorsByBubbleId[bubbleId] = cursors
	storage.set('cursorByBubbleId', cursorsByBubbleId)
	return cursors
}

export const setStartCursorForBubble = (bubbleId, start) => {
	const cursors = getCursorsForBubble(bubbleId)
	cursors.start = start
	return setCursorsForBubble(bubbleId, cursors)
}

export const setStopCursorForBubble = (bubbleId, stop) => {
	const cursors = getCursorsForBubble(bubbleId)
	cursors.stop = stop
	if (!cursors.start) {
		setStartCursorForBubble(bubbleId, stop)
	}
	return setCursorsForBubble(bubbleId, cursors)
}

export const importCursors = () => {
	try {
		const cached = storage.get('cursorByBubbleId')
		Object.assign(cursorsByBubbleId, cached ? cached : {})
	} catch (e) {
		console.error(e)
	}
}

export const setMessageAndMoveCursor = (message) => {
	const bubbleId = message.bubble_id || message.chat_id
	const ids = getMessageIdsForBubble(bubbleId).filter(Number.isInteger)
	ids.push(message.id)
	const id = ids.sort(sortMessageIds).slice(-1)[0]
	setStopCursorForBubble(bubbleId, id)
	setMessage(message)
}

// Thread chat getters (distinct from reply chain functions above)
// These relate to thread chats, not message parent/child relationships

// Check if a message has spawned a thread chat
export const hasThreadChat = (messageId) => {
	const message = getMessage(messageId)
	return !!message?.thread_id
}

// Get the thread chat ID for a message (if it spawned a thread)
export const getThreadChatIdForMessage = (messageId) => {
	const message = getMessage(messageId)
	return message?.thread_id || null
}

// Get all messages in a chat that have spawned thread chats
export const getMessagesWithThreadChats = (chatId) => {
	return getMessagesForBubble(chatId).filter((m) => m.thread_id)
}
